import { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useFirebase } from "@/contexts/FirebaseContext";
import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, getFirestore } from "firebase/firestore";
import { Star, MessageSquare, User, Award } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface StudentStory {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  instructorName: string;
  instructorId: string;
  rating: number;
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: any;
  updatedAt: any;
}

const StudentStories = () => {
  const { currentUser } = useFirebase();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userStories, setUserStories] = useState<StudentStory[]>([]);
  const [formData, setFormData] = useState({
    instructorName: "",
    instructorId: "",
    rating: 5,
    description: "",
  });

  // Fetch user's existing stories
  useEffect(() => {
    const fetchUserStories = async () => {
      if (!currentUser?.uid) return;

      try {
        setLoading(true);
        console.log("Fetching user stories for user:", currentUser.uid);
        const db = getFirestore();
        const storiesQuery = query(
          collection(db, "student_stories"),
          where("studentId", "==", currentUser.uid),
          orderBy("createdAt", "desc")
        );
        
        console.log("Executing user stories query...");
        const storiesSnapshot = await getDocs(storiesQuery);
        console.log("User stories snapshot:", storiesSnapshot.docs.length, "documents found");
        
        const stories = storiesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as StudentStory[];
        
        console.log("Processed user stories:", stories);
        setUserStories(stories);
      } catch (error) {
        console.error("Error fetching stories:", error);
        toast({
          title: "Error",
          description: "Failed to load your stories.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserStories();
  }, [currentUser, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser?.uid || !currentUser?.email) {
      toast({
        title: "Error",
        description: "Please log in to submit a story.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.instructorName.trim() || !formData.description.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);
      const db = getFirestore();
      
      const newStory = {
        studentId: currentUser.uid,
        studentName: currentUser.displayName || "Student",
        studentEmail: currentUser.email,
        instructorName: formData.instructorName.trim(),
        instructorId: formData.instructorId || "",
        rating: formData.rating,
        description: formData.description.trim(),
        status: 'pending' as const,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "student_stories"), newStory);

      toast({
        title: "Success",
        description: "Your story has been submitted successfully! It will be reviewed and published soon.",
      });

      // Reset form
      setFormData({
        instructorName: "",
        instructorId: "",
        rating: 5,
        description: "",
      });

      // Add the new story to the local state immediately
      const newStoryWithId = {
        ...newStory,
        id: "temp-" + Date.now(), // Temporary ID for display
        status: 'pending' as const,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      setUserStories(prevStories => [newStoryWithId, ...prevStories]);
    } catch (error) {
      console.error("Error submitting story:", error);
      toast({
        title: "Error",
        description: "Failed to submit your story. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? "text-yellow-400 fill-current" : "text-gray-300"
            }`}
          />
        ))}
        <span className="ml-2 text-sm text-gray-600">({rating}/5)</span>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="flex items-center justify-center h-64">
          <p>Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Student Stories</h1>
          <p className="text-gray-600 mt-2">
            Share your success story and rate your instructor
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Submit New Story */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Submit Your Story
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="instructorName">Instructor Name *</Label>
                  <Input
                    id="instructorName"
                    value={formData.instructorName}
                    onChange={(e) => setFormData({ ...formData, instructorName: e.target.value })}
                    placeholder="Enter your instructor's name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rating">Rating *</Label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating: star })}
                        className="focus:outline-none"
                      >
                        <Star
                          className={`h-6 w-6 transition-colors ${
                            star <= formData.rating
                              ? "text-yellow-400 fill-current"
                              : "text-gray-300 hover:text-yellow-300"
                          }`}
                        />
                      </button>
                    ))}
                    <span className="ml-2 text-sm text-gray-600">
                      ({formData.rating}/5)
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Your Story *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Share your experience, what you learned, and how your instructor helped you succeed..."
                    rows={6}
                    required
                  />
                  <p className="text-xs text-gray-500">
                    Tell us about your learning journey, the challenges you faced, and how your instructor helped you overcome them.
                  </p>
                </div>

                <Button type="submit" disabled={submitting} className="w-full">
                  {submitting ? "Submitting..." : "Submit Story"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Your Stories */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Your Stories
              </CardTitle>
            </CardHeader>
            <CardContent>
              {userStories.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Stories Yet
                  </h3>
                  <p className="text-gray-500">
                    Share your first success story to inspire others!
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {userStories.map((story) => (
                    <div
                      key={story.id}
                      className="border rounded-lg p-4 space-y-3"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {story.instructorName}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {story.createdAt?.toDate ? 
                              story.createdAt.toDate().toLocaleDateString() : 
                              new Date().toLocaleDateString()
                            }
                          </p>
                        </div>
                        {getStatusBadge(story.status)}
                      </div>

                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {story.studentName}
                        </span>
                      </div>

                      {renderStars(story.rating)}

                      <div className="text-sm text-gray-700">
                        {story.description}
                      </div>

                      {story.status === 'rejected' && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                          <p className="text-sm text-red-700">
                            Your story was not approved. Please review the guidelines and try again.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Guidelines */}
        <Card>
          <CardHeader>
            <CardTitle>Story Guidelines</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-600">
              <p>
                <strong>What to include in your story:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Your learning journey and experience</li>
                <li>How your instructor helped you succeed</li>
                <li>Challenges you faced and how you overcame them</li>
                <li>What you learned and how it benefited you</li>
                <li>Any tips or advice for other students</li>
              </ul>
              <p>
                <strong>Note:</strong> All stories are reviewed before being published on our website. 
                Stories that are inappropriate or don't follow our guidelines may be rejected.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default StudentStories; 